/**
 * `/o/<organizationId>` — the organization's own hub: who this is, what they have on, and one way
 * to give to them outside any of it.
 *
 * ## What changed, and the defect that forced it (GH-783)
 *
 * The first version of this page knew about exactly **one** event: whichever one the directory's
 * `LIVE_EVENT` lateral picked. So an organization with a fundraiser tonight, a dinner next month
 * and three finished campaigns arrived on a donor's phone as an organization with one thing
 * happening, no calendar, no record, and nobody's name on it. The owner's rule is that this page
 * is about the **organization** — its record, its people, its events — and that giving *to the
 * organization* is a different act from giving *to tonight*.
 *
 * That distinction is not presentational. `LIVE_EVENT` orders `is_general_fund` **last**, so
 * `liveEventId` is tonight's fundraiser whenever one is running. A button labelled "General fund"
 * that minted a checkout from `liveEventId` would put a fund on the donor's receipt that they did
 * not choose. The server therefore names the general fund separately — `generalFundEventId` on the
 * detail payload — and `OrgGive` presses that id and no other. Two names, two destinations, and
 * neither one is inferred here from the other.
 *
 * ## The division of labour with `/e/<eventId>`
 *
 * This page gives to the **general fund**. An event's gift is made on the event's own page, which
 * every row here links to. That is why there is no "Give" button for the main event on this screen
 * any more: a donor who came for tonight goes to tonight's page, where the total, the poll, the
 * raffle and the check-in already are (`EventPage.tsx`). A second Give here, minting for a
 * different event than the button two rows above it, is precisely the confusion the defect above
 * describes.
 *
 * ## Three guarantees this rewrite must not lose
 *
 * **It mints nothing.** Only the server signs a checkout context (SPEC.md §5.1), and this page
 * does not ask it to until the donor presses the button. Opening this page and leaving must cost
 * nothing — the three reads it performs are public, cacheable, and identifier-free.
 *
 * **It never shows a give button the server would refuse.** Both conditions are the server's: the
 * organization must be permitted to accept donations, and a general fund must exist (migration
 * 0026 creates one on approval). An unclaimed record and an organization between campaigns are
 * ordinary states (SPEC.md §6.1), and each says so in the server's own wording rather than in an
 * error.
 *
 * **Check-in is offered only where check-in works.** `POST /v1/donor/check-ins` refuses any event
 * that is not `live`, so the link appears on a live row and nowhere else — see `OrgEventList`.
 *
 * ## Three reads, three independent failures
 *
 * The detail read decides the page; a failure there is the only one that blanks it. The goal and
 * the event lists are each fetched separately and each fail silently into absence, because a
 * thermometer that would not load and a calendar that would not load are both decorations next to
 * the thing a donor came here to do. The same rule the first version of this file already applied
 * to the goal alone.
 */
import { useEffect, useState } from "react";
import {
  formatMoney,
  type GoalResponse,
  type OrganizationDetailResponse,
  type OrganizationEventsResponse,
} from "@givehere/contracts";
import { fetchGoal, fetchOrganizationDetail, fetchOrganizationEvents } from "./api.js";
/* Was a private `headline()` in this file whose own comment read "Verbatim from live-display" —
   the third copy of one arithmetic rule about money, and the event page would have been a fourth.
   The rule now lives in one module for this bundle (GH-781); the behaviour is unchanged. */
import { headline } from "./goal-math.js";
import { echoes } from "./copy-echo.js";
import { EventsForMembers } from "./EventAccess.js";
import { OrgEventList } from "./OrgEventList.js";
import { hasOrgFacts, OrgFacts } from "./OrgFacts.js";
import { OrgGive } from "./OrgGive.js";
import { OrgMark } from "./OrgMark.js";
import { OrgPeople } from "./OrgPeople.js";
import { ShareIconButton } from "./ShareControls.js";
import { Explain } from "./Explain.js";
import { identityKeyline } from "./identity-keyline.js";

export function OrgLanding({ organizationId }: { organizationId: string }) {
  const [detail, setDetail] = useState<OrganizationDetailResponse | null>(null);
  const [events, setEvents] = useState<OrganizationEventsResponse | null>(null);
  const [goal, setGoal] = useState<GoalResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;

    fetchOrganizationDetail(organizationId)
      .then((value) => {
        if (!live) return;
        setDetail(value);
        /*
          The thermometer belongs to the main event and to nothing else.

          Two conditions, both the server's. The organization must be able to accept donations —
          the figure describes money given *through GiveHere*, and an organization that has taken
          none would otherwise show a total directly above "hasn't set up giving on GiveHere yet".
          And the event must be a real occasion rather than the always-on general fund, whose goal
          is zero by construction (migration 0026): a bar with no target is not a bar, and
          `LIVE_EVENT` hands us the general fund whenever nothing else is running.
        */
        const mainEventId = mainEventIdOf(value);
        if (!value.organization.canAcceptDonations || mainEventId === null) return;
        // Separately, and separately fallible. The goal is the most interesting thing on this page
        // and the least essential: somebody who came to give can give without knowing how far
        // along the night is, and a page that refused to render its button because a thermometer
        // would not load would be withholding the product over a decoration.
        void fetchGoal(mainEventId).then(
          (value) => live && setGoal(value),
          () => undefined,
        );
      })
      .catch(() => live && setFailed(true));

    /*
      Started at the same moment as the detail rather than after it, and deliberately not gated on
      what the detail says. Gating would make the calendar a second round trip in series behind the
      first, which is the one shape a page on a hall's wifi cannot afford; and the only saving
      would be on an unclaimed organization, where the answer is two empty arrays from a cached,
      identifier-free public read.
    */
    void fetchOrganizationEvents(organizationId).then(
      (value) => live && setEvents(value),
      () => undefined,
    );

    return () => {
      live = false;
    };
  }, [organizationId]);

  if (failed) {
    return (
      <main className="card card--landing">
        <p className="notice notice--error" role="alert">
          This page could not be loaded. Try again, or search the directory.
        </p>
        <a className="landing__quiet" href="/">
          Back to the directory
        </a>
      </main>
    );
  }

  if (detail === null) {
    return (
      <main className="card card--landing">
        <p className="notice">Loading…</p>
      </main>
    );
  }

  const { organization } = detail;

  /*
    The main event, as the owner names it: the one thing this organization has running now, which
    is what `LIVE_EVENT` picks — except when what it picked is the general fund, which is not an
    occasion at all. A general fund has no room to arrive at, no admission to sell and nothing
    running (`directory-sections.ts` says the same sentence about the directory's own rows), so
    here it is simply not an event, and the page falls through to the calendar below.
  */
  const mainEventId = mainEventIdOf(detail);
  const mainEvent = mainEventId !== null ? detail.event : null;

  /*
    The main event appears in `upcoming` too — it is published or live, and it is not a general
    fund, so both arms of the list route select it. Rendered once, above, with its progress; the
    list skips it rather than showing the same evening twice under two different treatments.
  */
  const upcoming = (events?.upcoming ?? []).filter((event) => event.id !== mainEventId);
  /*
    Running now, separated from merely scheduled (owner-directed 2026-09-02: `/o` "is not showing
    all the live events on the page").

    `upcoming` is one bucket by construction — the list route selects `published` and `live`
    together and orders them by start time — so an evening happening *right now* sorted into the
    same list as one announced for next month, distinguishable only by a date the reader has to
    compare against today's. An organization running three things at once therefore looked like an
    organization with a calendar.

    The split is on the server's own `status`, never on the clock: a `starts_at` in the past does
    not mean an event is running (an organizer may publish one and never start it), and `live` is
    the only status the check-in route will actually accept. That is the same fact that decides
    which rows carry a check-in link — see `OrgEventList`.

    The main event is already above with its progress and is excluded from both lists, so nothing
    is drawn twice.
  */
  const stillRecent = (events?.recent ?? []).filter((event) => event.id !== mainEventId);
  /*
    Live rows are collected from **both** buckets, because the server's `recent` arm is the one
    place the clock does classify: it takes `published`/`live` rows whose `ends_at` has passed. An
    organizer who never presses "end" therefore had an event that was still `live` — still
    accepting check-ins, still accepting gifts — sitting under a heading that said it was over,
    while its own row offered a check-in link. The heading and the link disagreed, and the link was
    the one telling the truth.

    So the rule is the one stated above and now actually applied: **`status` decides, never the
    clock.** An event the server will still accept a check-in for is happening now, whichever
    bucket it arrived in.
  */
  const liveNow = [...upcoming, ...stillRecent].filter((event) => event.status === "live");
  const comingUp = upcoming.filter((event) => event.status !== "live");
  const recent = stillRecent.filter((event) => event.status !== "live");
  /*
    "Events · N", near the top (GH-793/GH-800 parity with iOS, GH-809): every event this page lists,
    the main one included, so the count matches what the jump lands on. Absent while the list is
    loading or failed, and for an organization with none — a count of nothing is not a link.
  */
  const eventCount = (mainEvent ? 1 : 0) + liveNow.length + comingUp.length + recent.length;
  /*
    Said only when it is known to be true. `events === null` is both "still loading" and "the list
    read failed", and neither is grounds for telling a donor an organization has nothing on.
  */
  const nothingOn = mainEvent === null && events !== null && upcoming.length === 0;
  /*
    GH-871b: membership is on and this visitor is not a member, so the lists came back withheld
    (`eventsHidden`) — keeping only an event their access token opens. "Nothing running" would be
    untrue; the one members line says what is happening instead. Give is unchanged above it.
  */
  const eventsHidden = events?.eventsHidden === true;
  const mainSubtitle =
    mainEvent === null
      ? null
      : echoes(mainEvent.purpose, mainEvent.name)
        ? echoes(mainEvent.description, mainEvent.name)
          ? null
          : mainEvent.description
        : (mainEvent.purpose ?? mainEvent.description);

  /*
    The organization's 3px identity keyline (GH-853): its brand colour, and nothing else of it.
    Absent when it has not chosen one — see `identity-keyline.ts`.
  */
  const keyline = identityKeyline(organization.brandColor);

  return (
    <main className={`card card--landing ${keyline.className}`.trim()} style={keyline.style}>
      {/* The mark and the identity share a row — the lockup the owner asked for (2026-09-15,
          "name should be next to logo, separator after"), and the one the giving season used to
          compress into. `data-season` only while the server says the organization's declared
          window is open; all it adds now is the label above the name. */}
      <header className="landing__head" data-season={organization.season ? "" : undefined}>
        <OrgMark
          displayName={organization.displayName}
          logoUrl={organization.logoUrl}
          className="landing__mark"
          size={44}
        />
        <div className="landing__ident">
          {organization.season && (
            <p className="season-label" dir="auto">
              {organization.season.label}
            </p>
          )}
          <h1 className="landing__name" dir="auto">
            {organization.displayName}
          </h1>
        </div>
        {/*
          The server's own verification wording, never a badge and never a claim this page
          composed. It is a statement about a registry record, and rewording it here would be the
          client asserting something the server did not.
        */}
        <p className="landing__meta">
          {[organization.verificationLabel, organization.locality].filter(Boolean).join(" · ")}
        </p>
        {/* GH-864e: hidden while membership is off; a member's link carries their share code. */}
        <ShareIconButton
          target={{ organizationId: organization.id }}
          name={organization.displayName}
        />
      </header>

      {events !== null && eventCount > 0 && (
        <a className="link landing__eventsjump" href="#events">
          Events · {eventCount}
        </a>
      )}

      {goal && (
        <section className="landing__goal">
          <p className="landing__figure">
            {/*
              The headline the room watches, composed by exactly the rule `live-display` uses so a
              donor looking from this page to the projector and back does not see two different
              nights: received, plus active pledges when the event counts them. The server owns
              every number; what it does not send is which of them the operator chose to
              celebrate, so it sends the flag and this renders the choice.
            */}
            <strong>{formatMoney(headline(goal))}</strong>
            <span> of {formatMoney(goal.goal)}</span>
          </p>
          {/*
            Received and pledged named separately and never added together. `headline` may already
            fold pledges in when the organization asked for that — the operator's choice to
            celebrate promises, rendered rather than invented — but folding is only safe while the
            parts stay visible underneath. A donor who wants to know what has actually arrived gets
            the answer on the next line rather than nowhere.
          */}
          <p className="landing__split">
            Received {formatMoney(goal.received)}
            {BigInt(goal.pledged.amountMinor) > 0n && (
              <> · Pledged {formatMoney(goal.pledged)}</>
            )} · {goal.donationCount} {goal.donationCount === 1 ? "gift" : "gifts"}
          </p>
        </section>
      )}

      {mainEvent && (
        <>
          <h2 className="landing__event" dir="auto">
            {mainEvent.name}
          </h2>
          {/*
            Dropped when it only repeats the heading above it. An organizer may name an event and
            state its purpose in the same words — the console permits it and real records do it —
            and the block then printed those words twice, once large and once small, which reads
            as a rendering fault rather than as an organization with a short answer. The event page
            and every calendar row apply the same rule.
          */}
          {mainSubtitle !== null && (
            <p className="landing__about" dir="auto">
              {mainSubtitle}
            </p>
          )}
          {/*
            The two doors into tonight. Giving to *this event* happens on the event's own page —
            that page holds the total, the poll, the raffle and the check-in, and it is where the
            server's own gates on the event's status are already applied.
          */}
          <p className="orgevent__links landing__mainlinks">
            <a className="link" href={`/e/${encodeURIComponent(mainEvent.id)}`}>
              Open the event page
            </a>
            {mainEvent.status === "live" && (
              <a className="link" href={`/c/${encodeURIComponent(mainEvent.id)}`}>
                Check in
              </a>
            )}
          </p>
        </>
      )}

      {eventsHidden && <EventsForMembers />}

      {nothingOn && !eventsHidden && organization.canAcceptDonations && (
        <Explain className="notice" label="Nothing running right now" title="Between campaigns">
          {/* The organization is named inside the panel rather than in the visible line — a
              tenant-authored name mid-sentence is also the five words' whole budget, and it
              already reads its own direction in the header above. */}
          <p>
            {`${organization.displayName} isn't running anything at the moment. It will appear here when they are.`}
          </p>
        </Explain>
      )}

      {/*
        The one control on this page that touches money, in whichever of its two shapes the
        server's answers select. Its own file because both shapes share one busy flag and one
        rule — see `OrgGive` for why it presses `generalFundEventId` and never `liveEventId`.
      */}
      <OrgGive organization={organization} heldGifts={detail.heldGifts} />

      {/*
        The jump's landing: live now, then upcoming by date (the server's soonest-first order), then
        past — the iOS organization page's three groups, under its names (GH-800, GH-809).
      */}
      <div id="events" className="landing__events">
        <OrgEventList heading="Live now" events={liveNow} />
        <OrgEventList heading="Upcoming" events={comingUp} />
        <OrgEventList heading="Past" events={recent} />
      </div>
      <OrgPeople people={detail.people} />

      {/*
        The organization's own account of itself, and then its record. Below the calendar on
        purpose: a donor deciding *whether* wants the record, a donor deciding *what* wants the
        calendar, and the second question is the one somebody who reached this page already has.
        The description is org-authored prose, so the five-word rule does not cap it — that rule is
        about copy this product writes.
      */}
      {(organization.description || hasOrgFacts(organization)) && (
        <section className="landing__block">
          <h2 className="landing__section">About</h2>
          {organization.description && (
            <p className="landing__about" dir="auto">
              {organization.description}
            </p>
          )}
          <OrgFacts organization={organization} />
        </section>
      )}

      {/* `link` for the colour and the underline: `landing__quiet` only positions it, so without
          this the one link at the foot of the page rendered in the browser's default blue while
          every other link on it was the accent green. */}
      <a className="link landing__quiet" href="/">
        Back to the directory
      </a>
    </main>
  );
}

/**
 * The main event: the one thing this organization has **on right now**, or nothing (GH-809).
 *
 * `liveEventId` is the directory's pick, and since GH-800 that pick may be the soonest *upcoming*
 * event when nothing is running — the directory lists announced events too. Treating it as "what
 * is happening" put next month's dinner at the top of the page with a thermometer, as though it
 * were tonight. So the main event needs the server's own effective status to say `live`; an
 * upcoming pick falls through to the Upcoming list below, and the general fund is never an
 * occasion at all.
 */
function mainEventIdOf(detail: OrganizationDetailResponse): string | null {
  const { organization, event } = detail;
  if (organization.liveEventIsGeneralFund || organization.liveEventId === null) return null;
  if (!event || event.id !== organization.liveEventId || event.status !== "live") return null;
  return organization.liveEventId;
}
